import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DeepPartial } from '@models/utils.model';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { HomeModel } from '@models/home.model';
import { catchError, of } from 'rxjs';
import { SharedActions } from '@states/shared/shared.action-types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { analyticModesStr, trainingModeStr } from '@models/camera.model';
import { EnableEscCloseDirective } from '../../../../../../../../shared/directives/enable-esc-close.directive';
import {
  EdgeCamera,
  UpdateCameraStreamDocument,
} from '../../../../../../../../cameras/camera.model';
import { HomeService } from '../../../../../../../../development/home.service';
import { ConfigurationTabComponentV2 } from '../configuration-tab/configuration-tab-component-v2.component';
import { IfDeveloperDirective } from '../../../../../../../../directives/if-developer.directive';
import { MatOption, MatSelect } from '@angular/material/select';
import { NgIf } from '@angular/common';
import { UiKitModule } from '../../../../../../../../shared/ui-kit/ui-kit.module';
import { CameraStorageSettingsTabComponentV2 } from '../camera-storage-settings-tab/camera-storage-settings-tab-v2.component';
import { TagSelectComponent } from '../../../../../../../database/pages/tags/components/tag-select/tag-select.component';
import { LocationActions } from '@states/location/location.action-types';
import { UiButtonComponent } from '@ui';
import { LiveViewTabComponent } from '../../../../../../../../pages/camera-edit/tabs/live-view-tab/live-view-tab.component';
import { LiveViewDirtyUpdatePayload } from '../../../../../../../../pages/camera-edit/tabs/live-view-tab/live-view-tab.model';

@Component({
  selector: 'app-bulk-update-modal',
  templateUrl: './bulk-update-form.component.html',
  styleUrl: './bulk-update-form.component.scss',
  hostDirectives: [EnableEscCloseDirective],
  imports: [
    ReactiveFormsModule,
    IfDeveloperDirective,
    TranslatePipe,
    MatSelect,
    MatOption,
    CameraStorageSettingsTabComponentV2,
    ConfigurationTabComponentV2,
    LiveViewTabComponent,
    NgIf,
    UiKitModule,
    MatDialogClose,
    UiButtonComponent,
    TagSelectComponent,
  ],
})
export class BulkUpdateFormComponent implements OnInit {
  @ViewChild('storageSettings')
  storageSettings: CameraStorageSettingsTabComponentV2;
  @ViewChild('configurationSettings')
  configurationSettings: ConfigurationTabComponentV2;
  @ViewChild('liveViewSettings') liveViewSettings: LiveViewTabComponent;

  public AnalyticModes = EdgeCamera.AnalyticModes;
  public AnalyticTrainingMode = EdgeCamera.AnalyticTrainingMode;
  public analyticModesStr = analyticModesStr;
  public trainingModeStr = trainingModeStr;

  public updatedDocument: DeepPartial<EdgeCamera.CameraDocument> = {};
  form: FormGroup = this.fb.group({
    disabled: [null],
    analyticMode: [null],
    trainingMode: [null],
    sdCardEnabled: [null],
    username: [null],
    password: [null],
    mainStreamPort: [null],
    mainStreamRtsp: [null],
    subStreamEnabled: [null],
    subStreamPort: [null],
    subStreamRtsp: [null],
    forceTcp: [null],
    protectiveGear: [null],
    forklift: [null],
    container: [null],
    shoppingCart: [null],
    zoneDwell: [null],
    advancedFallDetection: [null],
    fps: [null],
    tags: [[]],
  });
  public loading = false;
  protected readonly Object = Object;
  private modifiedFields: Set<string> = new Set();
  private updatedStorage: any = {};
  private updatedConfiguration: any = {};
  private updatedAudioConfiguration: any = {};
  private updatedLiveViewConfiguration: LiveViewDirtyUpdatePayload = {};

  constructor(
    private fb: FormBuilder,
    private store$: Store,
    private dialogRef: MatDialogRef<BulkUpdateFormComponent>,
    private homeService: HomeService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      camerasToManage: HomeModel.Camera[];
    },
  ) {}

  public get numberOfFieldsToUpdate() {
    return (
      this.modifiedFields.size +
      Object.keys(this.updatedStorage).length +
      Object.keys(this.updatedConfiguration).length +
      Object.keys(this.updatedAudioConfiguration).length +
      this.countLiveViewDirtyFields()
    );
  }

  private countLiveViewDirtyFields(): number {
    let count = 0;
    if (this.updatedLiveViewConfiguration.hqConfig) {
      count += 1;
    }
    if (this.updatedLiveViewConfiguration.mqConfig) {
      count += 1;
    }
    if (this.updatedLiveViewConfiguration.sqConfig) {
      count += 1;
    }
    return count;
  }

  ngOnInit(): void {
    // Track changes for each form control
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.valueChanges.subscribe((value) => {
        if (key === 'tags') {
          if (Array.isArray(value) && value.length > 0) {
            this.modifiedFields.add(key);
          } else {
            this.modifiedFields.delete(key);
          }
        } else if (value !== null) {
          this.modifiedFields.add(key);
        } else if (this.modifiedFields.has(key)) {
          this.modifiedFields.delete(key);
        }
        this.updateDocument();
      });
    });
    // Add modified field styles
    this.addModifiedFieldStyles();
  }

  public storageChanged() {
    // Get the current storage settings
    const newStorage = this.storageSettings?.updatedStorage;

    // Only keep defined values
    if (newStorage) {
      this.updatedStorage = Object.entries(newStorage).reduce(
        (acc, [key, value]) => {
          if (
            key === 'subStreams' &&
            Array.isArray(value) &&
            value.every(
              (item) => item == null || Object.keys(item).length === 0,
            )
          ) {
            return acc;
          }

          // Skip null/undefined values and empty arrays
          if (value != null && !(Array.isArray(value) && value.length === 0)) {
            // Handle nested objects like smartStorage, cloudStorage, etc.
            if (typeof value === 'object' && !Array.isArray(value)) {
              const nestedObj = Object.entries(value).reduce(
                (nested, [nestedKey, nestedValue]) => {
                  if (
                    nestedValue != null &&
                    !(Array.isArray(nestedValue) && nestedValue.length === 0)
                  ) {
                    nested[nestedKey] = nestedValue;
                  }
                  return nested;
                },
                {},
              );

              // Only add nested object if it has properties
              if (Object.keys(nestedObj).length > 0) {
                acc[key] = nestedObj;
              }
            } else {
              acc[key] = value;
            }
          }
          return acc;
        },
        {},
      );
      if (!this.updatedStorage.additionalEnabled) {
        delete this.updatedStorage?.cloudStorage;
        delete this.updatedStorage?.externalStorage;
      } else {
        // If additionalEnabled is true remove cloudStorage or externalStorage if they are not enabled
        if (!this.updatedStorage?.cloudStorage?.enabled) {
          delete this.updatedStorage?.cloudStorage;
        }
        if (!this.updatedStorage?.externalStorage?.enabled) {
          delete this.updatedStorage?.externalStorage;
        }
      }
      console.log(this.updatedStorage);
    }
  }

  public configurationChanged() {
    const newConfiguration = this.configurationSettings?.updatedConfiguration;
    const audioConfiguration =
      this.configurationSettings?.updatedAudioConfiguration;

    if (newConfiguration) {
      const { hqConfig, mqConfig, sqConfig, ...nonEncodeConfiguration } =
        newConfiguration;
      this.updatedConfiguration = { ...nonEncodeConfiguration };
      console.log('Updated configuration:', this.updatedConfiguration);
    }
    if (audioConfiguration) {
      if (
        this.updatedAudioConfiguration.mainStreamEnabled === false &&
        (audioConfiguration.mainStreamEnabled === true ||
          audioConfiguration.mainStreamEnabled === undefined)
      ) {
        this.updatedAudioConfiguration = {
          ...this.updatedAudioConfiguration,
          mainStreamEnabled: true,
        };
      } else if (
        this.updatedAudioConfiguration.subStreamEnabled === false &&
        (audioConfiguration.subStreamEnabled === true ||
          audioConfiguration.subStreamEnabled === undefined)
      ) {
        this.updatedAudioConfiguration = {
          ...this.updatedAudioConfiguration,
          subStreamEnabled: true,
        };
      } else {
        this.updatedAudioConfiguration = {
          ...this.updatedAudioConfiguration,
          ...audioConfiguration,
        };
      }
      console.log(
        'Updated audio configuration:',
        this.updatedAudioConfiguration,
      );
    }
  }

  public liveViewChanged(): void {
    const liveViewConfiguration =
      this.liveViewSettings?.updatedLiveViewConfiguration;
    if (liveViewConfiguration) {
      this.updatedLiveViewConfiguration = { ...liveViewConfiguration };
      console.log(
        'Updated live view configuration:',
        this.updatedLiveViewConfiguration,
      );
    }
  }

  public updateDocument() {
    this.updatedDocument = {}; // Reset document to rebuild from current state
    const formValue = this.form.getRawValue();

    // Basic settings
    if (this.modifiedFields.has('disabled')) {
      this.updatedDocument.disabled = formValue.disabled;
    }

    if (this.modifiedFields.has('analyticMode')) {
      this.updatedDocument.analyticMode = formValue.analyticMode;
    }

    if (this.modifiedFields.has('sdCardEnabled')) {
      this.updatedDocument.sdCardEnabled = formValue.sdCardEnabled;
    }

    // Credentials
    if (this.modifiedFields.has('username')) {
      this.updatedDocument.username = formValue.username;
    }

    if (this.modifiedFields.has('password')) {
      this.updatedDocument.password = formValue.password;
    }

    // Main Stream settings
    if (
      this.modifiedFields.has('mainStreamPort') ||
      this.modifiedFields.has('mainStreamRtsp')
    ) {
      const mainStream: Partial<UpdateCameraStreamDocument> = {
        main: {},
      };

      if (this.modifiedFields.has('mainStreamPort')) {
        mainStream.main.port = formValue.mainStreamPort;

        if (this.modifiedFields.has('mainStreamPort')) {
          this.updatedDocument.networkDetails = {
            port: formValue.mainStreamPort,
          };
        }
      }

      if (this.modifiedFields.has('mainStreamRtsp')) {
        mainStream.main.connectionString = formValue.mainStreamRtsp;
      }

      this.updatedDocument.mainStream = mainStream;
    }

    // TCP settings
    if (this.modifiedFields.has('forceTcp')) {
      this.updatedDocument.networkDetails = {
        ...this.updatedDocument.networkDetails,
        forceTcp: formValue.forceTcp,
      };
    }

    // Sub Stream settings
    if (this.modifiedFields.has('subStreamEnabled')) {
      if (!formValue.subStreamEnabled) {
        // Remove substreams if disabled
        this.updatedDocument.subStreams = [];
      } else {
        const hasSubstreamConfig =
          this.modifiedFields.has('subStreamPort') ||
          this.modifiedFields.has('subStreamRtsp');

        if (hasSubstreamConfig) {
          this.updatedDocument.subStreams = [
            {
              main: {},
            },
          ];

          if (this.modifiedFields.has('subStreamPort')) {
            this.updatedDocument.subStreams[0].main.port =
              formValue.subStreamPort;
          }

          if (this.modifiedFields.has('subStreamRtsp')) {
            this.updatedDocument.subStreams[0].main.connectionString =
              formValue.subStreamRtsp;
          }
        }
      }
    } else if (
      this.modifiedFields.has('subStreamPort') ||
      this.modifiedFields.has('subStreamRtsp')
    ) {
      // Handle case where only port or connection string is modified but enabled state is not
      this.updatedDocument.subStreams = [
        {
          main: {},
        },
      ];

      if (this.modifiedFields.has('subStreamPort')) {
        this.updatedDocument.subStreams[0].main.port = formValue.subStreamPort;
      }

      if (this.modifiedFields.has('subStreamRtsp')) {
        this.updatedDocument.subStreams[0].main.connectionString =
          formValue.subStreamRtsp;
      }
    }

    // Analytics settings
    const analyticsFields = [
      'protectiveGear',
      'forklift',
      'shoppingCart',
      'container',
      'zoneDwell',
      'advancedFallDetection',
      'fps',
    ];
    const hasAnalyticsChanges = analyticsFields.some((field) =>
      this.modifiedFields.has(field),
    );

    if (hasAnalyticsChanges) {
      this.updatedDocument.analytics = {};

      analyticsFields.forEach((field) => {
        if (this.modifiedFields.has(field)) {
          this.updatedDocument.analytics[field] = formValue[field];
        }
      });
    }

    // Tags (additive only - backend merges with existing camera tags)
    if (this.modifiedFields.has('tags')) {
      this.updatedDocument.tags = formValue.tags;
    }
  }

  close() {
    this.dialogRef.close();
  }

  async save() {
    this.loading = true;
    const cameras = this.data.camerasToManage;

    if (Object.keys(this.updatedAudioConfiguration).length) {
      if (this.updatedAudioConfiguration?.subStreamEnabled !== undefined) {
        if (!this.updatedDocument?.subStreams) {
          this.updatedDocument.subStreams = [
            {
              audioInputEnabled:
                this.updatedAudioConfiguration?.subStreamEnabled,
            },
          ];
        } else {
          this.updatedDocument.subStreams[0] = {
            ...this.updatedDocument.subStreams[0],
            audioInputEnabled: this.updatedAudioConfiguration?.subStreamEnabled,
          };
        }
      }
      if (this.updatedAudioConfiguration?.mainStreamEnabled !== undefined) {
        this.updatedDocument.mainStream = {
          audioInputEnabled: this.updatedAudioConfiguration?.mainStreamEnabled,
        };
      }
    }

    const cameraBulkUpdate: HomeModel.CameraBulkUpdateRequest = {
      cameras,
      ...this.updatedDocument,
      ...this.updatedStorage,
      ...this.updatedConfiguration,
      ...this.updatedLiveViewConfiguration,
    };
    console.log(cameraBulkUpdate);
    this.homeService
      .cameraBulkUpdate(cameraBulkUpdate)
      .pipe(
        catchError((err) => {
          console.error('Error during bulk update:', err);
          this.store$.dispatch(
            SharedActions.showMessage({
              error: this.translateService.instant(
                'TOASTS.CAMERAS_UPDATE_FAILED',
                {
                  count: this.data.camerasToManage.length,
                },
              ),
            }),
          );
          this.loading = false;
          return of(null); // Handle error as needed
        }),
      )
      .subscribe((res) => {
        if (!res) return;
        this.store$.dispatch(
          SharedActions.showMessageFromSocket({
            level: res.messageLevel,
            msg: res.message,
          }),
        );

        this.store$.dispatch(LocationActions.forceReloadLocations());
        this.store$.dispatch(LocationActions.GetLocations());
        this.loading = false;
        this.dialogRef.close(true);
      });
  }

  private addModifiedFieldStyles() {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.valueChanges.subscribe((value) => {
        const element = document.querySelector(`[formControlName="${key}"]`);
        if (element) {
          const isModified = Array.isArray(value)
            ? value.length > 0
            : value !== null && value !== '';
          if (isModified) {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });
    });
  }
}
